/**
 * Customer Detail Page
 *
 * Displays customer profile with:
 * - Customer information
 * - List of all assessments for this customer
 * - Assessment history timeline
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';

export default function CustomerDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (user?.contractor_id && id) {
      fetchCustomerData();
    }
  }, [user, id]);

  const fetchCustomerData = async () => {
    try {
      setLoading(true);

      // Fetch customer
      const { data: customerData, error: customerError } = await supabase
        .from('customers')
        .select('*')
        .eq('id', id)
        .eq('contractor_id', user.contractor_id)
        .single();

      if (customerError) throw customerError;

      if (!customerData) {
        setError('Customer not found or you do not have permission to view them.');
        return;
      }

      setCustomer(customerData);

      // Fetch customer's assessments
      const { data: assessmentsData, error: assessmentsError } = await supabase
        .from('assessments')
        .select(`
          *,
          users!assessments_technician_id_fkey (
            id,
            full_name
          )
        `)
        .eq('customer_id', id)
        .eq('contractor_id', user.contractor_id)
        .order('created_at', { ascending: false });

      if (assessmentsError) throw assessmentsError;

      setAssessments(assessmentsData || []);
    } catch (err) {
      console.error('Error fetching customer data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getRecommendationColor = (recommendation) => {
    switch (recommendation?.toLowerCase()) {
      case 'replace':
        return { bg: '#fee2e2', border: '#ef4444', text: '#991b1b' };
      case 'consider':
        return { bg: '#fef3c7', border: '#f59e0b', text: '#92400e' };
      case 'repair':
        return { bg: '#dbeafe', border: '#3b82f6', text: '#1e40af' };
      case 'maintain':
        return { bg: '#dcfce7', border: '#22c55e', text: '#14532d' };
      default:
        return { bg: '#f3f4f6', border: '#9ca3af', text: '#374151' };
    }
  };

  const getRecommendationIcon = (recommendation) => {
    switch (recommendation?.toLowerCase()) {
      case 'replace':
        return '🔴';
      case 'consider':
        return '🟡';
      case 'repair':
        return '🔵';
      case 'maintain':
        return '🟢';
      default:
        return '⚪';
    }
  };

  if (loading) {
    return (
      <div className="container" style={{ paddingTop: '40px' }}>
        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
          <div style={{ fontSize: '18px', color: '#64748b' }}>Loading customer...</div>
        </div>
      </div>
    );
  }

  if (error || !customer) {
    return (
      <div className="container" style={{ paddingTop: '40px' }}>
        <div style={{ maxWidth: '600px', margin: '0 auto' }}>
          <div className="card" style={{ textAlign: 'center', padding: '40px 20px' }}>
            <div style={{ fontSize: '64px', marginBottom: '20px' }}>⚠️</div>
            <div style={{ fontSize: '20px', fontWeight: '600', color: '#2c3e50', marginBottom: '10px' }}>
              {error || 'Customer Not Found'}
            </div>
            <div style={{ fontSize: '14px', color: '#64748b', marginBottom: '30px' }}>
              This customer may not exist or you may not have permission to view them.
            </div>
            <Link to="/customers" className="btn btn-primary">
              ← Back to Customers
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container" style={{ paddingTop: '40px' }}>
      <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
        {/* Back Button */}
        <div style={{ marginBottom: '20px' }}>
          <Link
            to="/customers"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 20px',
              background: 'white',
              border: '2px solid #e0e6ed',
              borderRadius: '8px',
              color: '#64748b',
              textDecoration: 'none',
              fontSize: '14px',
              fontWeight: '600',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.target.style.borderColor = '#667eea';
              e.target.style.color = '#667eea';
            }}
            onMouseLeave={(e) => {
              e.target.style.borderColor = '#e0e6ed';
              e.target.style.color = '#64748b';
            }}
          >
            ← Back to Customers
          </Link>
        </div>

        {/* Customer Info Card */}
        <div className="card" style={{ marginBottom: '30px' }}>
          <h2 style={{ margin: '0 0 20px 0', fontSize: '24px', color: '#2c3e50' }}>
            {customer.full_name}
          </h2>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
            {customer.email && (
              <div>
                <div style={{ fontSize: '14px', color: '#64748b', marginBottom: '4px' }}>
                  Email
                </div>
                <div style={{ fontSize: '16px', color: '#2c3e50' }}>
                  <a href={`mailto:${customer.email}`} style={{ color: '#667eea', textDecoration: 'none' }}>
                    {customer.email}
                  </a>
                </div>
              </div>
            )}

            {customer.phone && (
              <div>
                <div style={{ fontSize: '14px', color: '#64748b', marginBottom: '4px' }}>
                  Phone
                </div>
                <div style={{ fontSize: '16px', color: '#2c3e50' }}>
                  <a href={`tel:${customer.phone}`} style={{ color: '#667eea', textDecoration: 'none' }}>
                    {customer.phone}
                  </a>
                </div>
              </div>
            )}

            {customer.property_address && customer.property_address !== 'TBD' && (
              <div>
                <div style={{ fontSize: '14px', color: '#64748b', marginBottom: '4px' }}>
                  Property Address
                </div>
                <div style={{ fontSize: '16px', color: '#2c3e50' }}>
                  {customer.property_address}
                </div>
              </div>
            )}

            <div>
              <div style={{ fontSize: '14px', color: '#64748b', marginBottom: '4px' }}>
                Customer Since
              </div>
              <div style={{ fontSize: '16px', color: '#2c3e50' }}>
                {new Date(customer.created_at).toLocaleDateString()}
              </div>
            </div>

            <div>
              <div style={{ fontSize: '14px', color: '#64748b', marginBottom: '4px' }}>
                Total Assessments
              </div>
              <div style={{ fontSize: '24px', fontWeight: '700', color: '#667eea' }}>
                {assessments.length}
              </div>
            </div>
          </div>
        </div>

        {/* Assessment History */}
        <div className="card">
          <h3 style={{ margin: '0 0 20px 0', fontSize: '20px', color: '#2c3e50' }}>
            Assessment History
          </h3>

          {assessments.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 20px', color: '#64748b' }}>
              <div style={{ fontSize: '48px', marginBottom: '15px' }}>📋</div>
              <div style={{ fontSize: '16px' }}>No assessments yet for this customer</div>
            </div>
          ) : (
            <div style={{ display: 'grid', gap: '12px' }}>
              {assessments.map((assessment) => {
                const colors = getRecommendationColor(assessment.recommendation);
                const icon = getRecommendationIcon(assessment.recommendation);

                return (
                  <div
                    key={assessment.id}
                    onClick={() => navigate(`/assessments/${assessment.id}`)}
                    style={{
                      padding: '16px',
                      background: '#f8fafc',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      border: '2px solid transparent'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#f0f4f8';
                      e.currentTarget.style.borderColor = '#667eea';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = '#f8fafc';
                      e.currentTarget.style.borderColor = 'transparent';
                    }}
                  >
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '15px', alignItems: 'center' }}>
                      <div>
                        <div style={{ fontSize: '16px', fontWeight: '600', color: '#2c3e50', marginBottom: '6px' }}>
                          {new Date(assessment.created_at).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                        </div>

                        <div style={{ display: 'flex', gap: '15px', fontSize: '13px', color: '#64748b' }}>
                          <span>👤 {assessment.users?.full_name || 'Unknown'}</span>
                          {assessment.system_age && <span>🏠 {assessment.system_age} yrs old</span>}
                          {assessment.assessment_score && <span>📊 Score: {assessment.assessment_score}/100</span>}
                        </div>
                      </div>

                      <div style={{
                        padding: '8px 16px',
                        background: colors.bg,
                        border: `2px solid ${colors.border}`,
                        borderRadius: '6px',
                        textAlign: 'center'
                      }}>
                        <div style={{ fontSize: '20px' }}>{icon}</div>
                        <div style={{
                          fontSize: '11px',
                          fontWeight: '700',
                          color: colors.text,
                          textTransform: 'uppercase',
                          marginTop: '2px'
                        }}>
                          {assessment.recommendation}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
